import styled from "styled-components";

import {
  weight,
  type,
  tracking,
  sectionPad,
  media,
  leading,
  font,
  container,
  color,
} from "../../../styles/tokens";

/* The section-composition vocabulary for every marketing page.
 *
 * Replaces pages/hebe/home/Section.jsx, which only four of the nine home components used — the rest
 * defined their own Container (at 1300px, 1400px, 980px and 900px) and their own heading styles.
 */

export const Section = styled.section`
  padding: ${sectionPad.y} ${sectionPad.x};
  background: ${(props) =>
    props.$tone === "alt"
      ? color.canvasAlt
      : props.$tone === "surface"
        ? color.surface
        : props.$tone === "ink"
          ? color.ink
          : color.canvas};
  color: ${(props) => (props.$tone === "ink" ? color.onDark : color.ink)};
`;

export const Container = styled.div`
  width: 100%;
  max-width: ${(props) => container[props.$width || "default"]};
  margin: 0 auto;
`;

export const SectionHeader = styled.div`
  max-width: 62ch;
  margin: 0 0 ${(props) => (props.$tight ? "40px" : "64px")};
  ${(props) => (props.$center ? "margin-inline: auto; text-align: center;" : "")}
`;

/* Small caps label above a heading. Gold is legible here only because it sits on a large-tracked
 * uppercase run at semibold — but it is still decorative, so goldText carries the actual colour
 * duty on light grounds.
 */
export const Eyebrow = styled.p`
  margin: 0 0 16px;
  font-family: ${font.body};
  font-size: ${type.eyebrow};
  font-weight: ${weight.semibold};
  letter-spacing: ${tracking.eyebrow};
  text-transform: uppercase;
  color: ${(props) => (props.$onDark ? color.gold : color.goldText)};
`;

export const Heading = styled.h2`
  margin: 0 0 20px;
  font-family: ${font.display};
  font-size: ${(props) => type[props.$size || "h2"]};
  font-weight: ${weight.regular};
  line-height: ${leading.tight};
  letter-spacing: ${tracking.tight};
  color: inherit;

  strong,
  em {
    font-weight: ${weight.semibold};
    font-style: normal;
  }
`;

export const Lede = styled.p`
  margin: 0;
  font-size: ${type.lg};
  line-height: ${leading.relaxed};
  color: ${(props) => (props.$onDark ? color.onDarkMuted : color.ink700)};

  ${media.md`
    font-size: ${type.base};
  `}
`;

export const Grid = styled.div`
  display: grid;
  gap: ${(props) => props.$gap || "32px"};
  grid-template-columns: repeat(${(props) => props.$cols || 3}, minmax(0, 1fr));

  ${media.lg`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${media.md`
    grid-template-columns: 1fr;
  `}
`;

/* A hairline rule in gold. The one place the highlight colour earns its keep at full strength. */
export const Rule = styled.hr`
  width: 56px;
  height: 2px;
  margin: 0 0 28px;
  border: 0;
  background: ${color.gold};
`;
